"use client";

import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useSession,
  useSupabaseClient,
} from "@/components/SupabaseProvider";
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { Session } from "@supabase/supabase-js";
import SparklesIcon from "./SparklesIcon";
import { useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import toast from "react-hot-toast";
import { REVIEW_USER_EMAILS } from "../const/webConst";
import { useMessages } from "../context/i18n";
import { videoInput as enVideoInput } from "../dictionaries/en/video-input";
import {
  identifyAndReloadFlags,
  waitForFeatureFlags,
} from "../helpers/posthogIdentify";
import {
  SIGNUP_FLOW_FLAG_KEY,
  type SignupFlowVariant,
  isReturningVisitor,
  signupFlowVariantFor,
} from "../helpers/signupFlowExperiment";
import { buildSignupRedirectUrl } from "../helpers/signupRedirect";
import { ModalContext } from "../hooks/useModal";
import { PROFILE_QUERY_KEY, fetchProfileByUserId } from "../hooks/useProfile";
import {
  VIDEO_METADATA_QUERY_KEY,
  fetchVideoUrlMetadata,
  getInaccessibleMessage,
} from "../hooks/useVideoInputMetadata";
import {
  VIDEO_STYLE_PRESETS_QUERY_KEY,
  getVideoStylePresetsByAuthorId,
} from "../hooks/useVideoStylePresets";
import { useRollingIndex } from "../hooks/useRollingIndex";
import { TaskOptions } from "../types/task";
import { parseVideoSource } from "@ziggapp/klap-core/video";
import Button from "./Button";
import type { SignupDialogInput } from "./SignupDialog";
import Spinner from "./Spinner";

const PLACEHOLDER_PROVIDERS = [
  "YouTube",
  "Twitch",
  "Vimeo",
  "Loom",
  "Google Drive",
  "Dropbox",
];

// Inline placeholder rendered while the dynamic ConfirmAction / SubmitVideoForm
// chunks download. Without it `dynamic()` returns `null` during chunk fetch,
// which made the modal pop empty for a beat after the user pastes a URL.
function ModalSpinner() {
  return (
    <div className="flex items-center justify-center py-12 min-h-[120px]">
      <Spinner className="w-6 h-6 text-slate-400" />
    </div>
  );
}

// Both of these are only rendered inside `modal.open(...)` after a user
// action (paste a URL, pick a file, etc.), so dynamic-loading them keeps the
// editor chunk (4435) out of the home/marketing route's initial preload.
const ConfirmAction = dynamic(() => import("./ConfirmAction"), {
  loading: () => <ModalSpinner />,
});
const SubmitVideoForm = dynamic(() => import("./SubmitVideoForm"), {
  loading: () => <ModalSpinner />,
});
// Same for the signup dialog signed-out visitors get on submit (it also
// carries AuthUI and the Turnstile widget).
const SignupDialog = dynamic(() => import("./SignupDialog"), {
  loading: () => <ModalSpinner />,
});

// Resolved components captured by `preloadDialogChunks`. The `dynamic()`
// wrappers above go through React.lazy, which suspends for a frame on
// first mount even when the chunk is already downloaded — that flashed
// the ModalSpinner inside the dialog. Rendering the resolved component
// directly avoids the suspense fallback entirely; the `dynamic()`
// wrappers remain only as a fallback if the preload failed.
let PreloadedConfirmAction:
  | (typeof import("./ConfirmAction"))["default"]
  | null = null;
let PreloadedSubmitVideoForm:
  | (typeof import("./SubmitVideoForm"))["default"]
  | null = null;
let PreloadedSignupDialog: (typeof import("./SignupDialog"))["default"] | null =
  null;

type VideoInputProps = {
  taskOptions?: TaskOptions;
  className?: string;
  disabled?: boolean;
  ctaLabel?: string;
  // Per-page placeholder text. The v2 SEO tool pages each pass a
  // tailored prompt (e.g. "Paste a YouTube URL for Shorts...") to
  // avoid wasting prime real estate on the generic default.
  placeholderText?: string;
} & ComponentPropsWithoutRef<"label">;

// const defaultTaskOptions: TaskOptions = [
//   {
//     key: "remove-silences",
//     label: "AI edit",
//     value: false,
//     tooltip:
//       "Remove bad takes and silences to turn your footage into a polished video",
//   },
//   {
//     key: "shorts",
//     value: true,
//     label: "Edit shorts",
//     tooltip: "Create up to 10 shorts from your video",
//   },
//   // {
//   //   key: "remove-silences",
//   //   label: "Remove silences",
//   //   value: false,
//   //   tooltip:
//   //     "Remove bad takes and silences to turn your footage into a polished video",
//   // },
//   {
//     key: "crop",
//     value: true,
//     label: "Crop video",
//     tooltip: "Crop your video to focus one the important parts",
//   },
//   {
//     key: "captions",
//     value: true,
//     label: "Add captions",
//     tooltip: "Add engaging captions to your video",
//   },
//   {
//     key: "emojis",
//     value: true,
//     label: "Add emojis",
//     tooltip: "Add relevant emojis to your video",
//   },
// ];

// type TaskOptionKey =
//   | "generate-captions"
//   | "video-to-shorts"
//   | "generate-emojis"
//   | "generate-crops";

const VideoInput = ({
  disabled,
  ctaLabel,
  placeholderText,
  ...rest
}: VideoInputProps) => {
  const m = useMessages("videoInput", enVideoInput);
  // const [value, setValue] = useState(props.value);
  // Read `?task=` lazily inside the submit handler instead of subscribing
  // via `useQueryParam` — this removes the need for `<QueryParamProvider>`
  // (and its `next-query-params/app` adapter, which calls
  // `useSearchParams()` and forced a Suspense boundary that streamed the
  // page metadata into the body and broke the SEO `meta-description`
  // audit). VideoInput doesn't need to re-render when the param changes;
  // it only needs the value at submit time.
  const readTaskParam = (): string | undefined => {
    if (typeof window === "undefined") return undefined;
    const search = new URLSearchParams(window.location.search);
    return search.get("task") ?? undefined;
  };
  const urlInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDropArea, setShowDropArea] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [value, setValue] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoadingDialog, setIsLoadingDialog] = useState(false);
  // Incremented to invalidate an in-flight dialog chunk preload when the
  // user cancels (clicks the x while loading) or submits again.
  const dialogLoadRequestRef = useRef(0);
  const modal = useContext(ModalContext);
  const posthog = usePostHog();
  const session = useSession();
  const router = useRouter();
  const queryClient = useQueryClient();
  const supabaseClient = useSupabaseClient();

  // Same key + fn useVideoMetadata queries with, so the hook joins this
  // request (or reads its result) instead of starting a new one. Covers
  // every provider: /video-details for worker providers, client-side
  // <video> probe + access check for dropbox/direct.
  const prefetchVideoMetadata = useCallback(
    (videoUrl: string) => {
      queryClient.prefetchQuery({
        queryKey: [VIDEO_METADATA_QUERY_KEY, "url", videoUrl],
        queryFn: () => fetchVideoUrlMetadata(videoUrl),
      });
    },
    [queryClient]
  );

  // Prepare everything the dialog needs *before* opening the modal, so it
  // never pops with the ModalSpinner placeholder or flickers between tabs:
  // - the dynamic SubmitVideoForm / ConfirmAction chunks
  // - the style presets + profile queries (pre-warmed into the same
  //   react-query cache entries the dialog's hooks read), so the initial
  //   Quick start / My templates tab landing resolves on the first frame.
  // While this runs, the input shows a spinner in place of the clear "x"
  // button. Returns false if the user cancelled while loading.
  //
  // For provider URLs, the video metadata fetch (`/video-details`:
  // duration, title, thumbnail, language) is also kicked off here — but
  // fire-and-forget, never awaited. In the common case it resolves during
  // the chunk download, so SubmitVideoForm's useVideoMetadata reads a warm
  // cache and the Source language row renders on the first frame. In the
  // slow case the modal still opens on the chunk schedule and shows its
  // own "Loading metadata" state, same as before.
  const preloadDialogChunks = useCallback(
    async (userId: string, videoUrl?: string) => {
      const requestId = ++dialogLoadRequestRef.current;
      setIsLoadingDialog(true);

      if (videoUrl) prefetchVideoMetadata(videoUrl);

      const chunksPromise = Promise.all([
        import("./SubmitVideoForm"),
        import("./ConfirmAction"),
      ]);

      const dataPromise = Promise.all([
        queryClient.prefetchQuery({
          queryKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, userId],
          queryFn: () => getVideoStylePresetsByAuthorId(supabaseClient, userId),
        }),
        queryClient.prefetchQuery({
          queryKey: [PROFILE_QUERY_KEY],
          queryFn: () => fetchProfileByUserId(supabaseClient, userId),
        }),
      ]);

      try {
        const [[submitVideoFormModule, confirmActionModule]] =
          await Promise.all([chunksPromise, dataPromise]);
        PreloadedSubmitVideoForm = submitVideoFormModule.default;
        PreloadedConfirmAction = confirmActionModule.default;
      } catch {
        // If the preload fails (flaky network), fall through and open the
        // dialog anyway: `dynamic()` will retry and show its placeholder,
        // and the dialog's own queries will fetch what's missing.
      }

      if (dialogLoadRequestRef.current !== requestId) return false;
      setIsLoadingDialog(false);
      return true;
    },
    [prefetchVideoMetadata, queryClient, supabaseClient]
  );

  // Signed-out counterpart of preloadDialogChunks: the signup dialog chunk,
  // plus the submit form's warmed in the background for after sign-in.
  const preloadSignupDialog = useCallback(
    async (videoUrl?: string) => {
      const requestId = ++dialogLoadRequestRef.current;
      setIsLoadingDialog(true);

      if (videoUrl) prefetchVideoMetadata(videoUrl);
      import("./SubmitVideoForm")
        .then((module) => {
          PreloadedSubmitVideoForm = module.default;
        })
        .catch(() => {});

      try {
        PreloadedSignupDialog = (await import("./SignupDialog")).default;
      } catch {
        // Same as above: `dynamic()` retries when the dialog renders.
      }

      if (dialogLoadRequestRef.current !== requestId) return false;
      setIsLoadingDialog(false);
      return true;
    },
    [prefetchVideoMetadata]
  );

  // Accessibility gate: never open the dialog for a link that can't be
  // processed (private Drive file, deleted Twitch VOD, ...). The check has
  // been running since the metadata prefetch, so this await is usually
  // instant; the input spinner covers the rest. Only a definitive
  // "inaccessible" verdict blocks — "unknown" (transient hiccup) opens the
  // dialog and the server re-validates at creation. Returns false when
  // blocked or cancelled via the x button.
  const passesAccessGate = useCallback(
    async (videoUrl: string) => {
      const gateRequestId = dialogLoadRequestRef.current;
      setIsLoadingDialog(true);
      const metadata = await queryClient
        .fetchQuery({
          queryKey: [VIDEO_METADATA_QUERY_KEY, "url", videoUrl],
          queryFn: () => fetchVideoUrlMetadata(videoUrl),
          staleTime: 5 * 60 * 1000,
        })
        .catch(() => null);
      if (dialogLoadRequestRef.current !== gateRequestId) return false; // cancelled
      setIsLoadingDialog(false);
      if (metadata?.access === "inaccessible") {
        const provider = parseVideoSource(videoUrl)?.provider;
        posthog?.capture("Inaccessible Link Blocked", {
          provider,
          reason: metadata.accessReason,
        });
        toast.error(
          getInaccessibleMessage(provider, metadata.accessReason),
          { duration: 8000 }
        );
        return false;
      }
      return true;
    },
    [posthog, queryClient]
  );

  // Opens the submit form. Takes the session as an argument rather than
  // reading `session` from this render: the signup dialog calls this (via
  // continueAfterSignIn) the moment the visitor signs in, through a callback
  // captured when it opened, before this component re-rendered signed in.
  // `beforeOpen` holds the form back (e.g. until feature flags reloaded)
  // while the chunks preload. Resolves to "opened", "cancelled" (the x, or
  // the signup dialog closed meanwhile) or "blocked" (inaccessible link).
  const openSubmitForm = useCallback(
    async (
      input: SignupDialogInput,
      activeSession: Session,
      beforeOpen?: Promise<unknown>
    ): Promise<"opened" | "cancelled" | "blocked"> => {
      // Pass the URL so the video metadata prefetch starts now, in
      // parallel with the chunk download.
      const preload = preloadDialogChunks(
        activeSession.user.id,
        input.type === "url" ? input.value : undefined
      );
      const requestId = dialogLoadRequestRef.current;
      const [ready] = await Promise.all([preload, beforeOpen]);
      // Cancelled via the x button (also while `beforeOpen` was pending).
      if (!ready || dialogLoadRequestRef.current !== requestId) {
        return "cancelled";
      }

      const ConfirmActionComponent = PreloadedConfirmAction ?? ConfirmAction;
      const SubmitVideoFormComponent =
        PreloadedSubmitVideoForm ?? SubmitVideoForm;

      if (input.type === "file") {
        modal.open(
          <SubmitVideoFormComponent input={input} defaultTask="shorts" />
        );
        return "opened";
      }

      if (!(await passesAccessGate(input.value))) {
        return dialogLoadRequestRef.current === requestId
          ? "blocked"
          : "cancelled";
      }

      const email = activeSession.user.email;
      if (email && REVIEW_USER_EMAILS.includes(email)) {
        // Open a confirmation dialog for link imports
        modal.open(
          <ConfirmActionComponent
            title="Confirm Video Usage Rights"
            description="By proceeding, you confirm that you own the copyright to this video or have obtained permission to use it."
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            onConfirm={async () => {
              modal.close();
              await new Promise((resolve) => setTimeout(resolve, 500));
              // Continue with the original function execution
              modal.open(
                <SubmitVideoFormComponent input={input} defaultTask="shorts" />
              );
            }}
            onCancel={() => {
              modal.close();
            }}
          />
        );
        return "opened";
      }

      modal.open(
        <SubmitVideoFormComponent input={input} defaultTask="shorts" />
      );
      return "opened";
    },
    [modal, passesAccessGate, preloadDialogChunks]
  );

  // Signed-in hand-off from the signup dialog. Identify first: the submit
  // form evaluates experiment flags (reframe focus, pricing limit) that
  // klap-api buckets by profile id, so it waits for the user's flags.
  const continueAfterSignIn = useCallback(
    async (input: SignupDialogInput, signedInSession: Session) => {
      const result = await openSubmitForm(
        input,
        signedInSession,
        identifyAndReloadFlags(posthog, signedInSession.user)
      );
      // Don't leave the signup dialog spinning on its "getting ready" step
      // (the access check has shown its toast). A cancel came from closing
      // the dialog, so there's nothing left to close.
      if (result === "blocked") modal.close();
    },
    [modal, openSubmitForm, posthog]
  );

  // Signed-out visitors sign up in a dialog over the page instead of being
  // sent to /signup, so the video they picked is still here once they're in
  // (see SignupDialog for each sign-in path).
  const openSignupDialog = useCallback(
    async (input: SignupDialogInput) => {
      const videoUrl = input.type === "url" ? input.value : undefined;
      const ready = await preloadSignupDialog(videoUrl);
      if (!ready) return; // cancelled via the x button
      // No point asking for an account for a link we can't process.
      if (videoUrl && !(await passesAccessGate(videoUrl))) return;

      const SignupDialogComponent = PreloadedSignupDialog ?? SignupDialog;
      modal.open(
        <SignupDialogComponent
          input={input}
          redirectTo={buildSignupRedirectUrl(window.location.origin, {
            videoUrl,
            task: readTaskParam(),
          })}
          onAuthenticated={(signedInSession) =>
            continueAfterSignIn(input, signedInSession)
          }
          onHandOffCancelled={() => {
            // Same as the input's x: invalidates the in-flight preload.
            dialogLoadRequestRef.current++;
            setIsLoadingDialog(false);
          }}
        />,
        { className: "max-w-sm" }
      );
    },
    [continueAfterSignIn, modal, passesAccessGate, preloadSignupDialog]
  );

  // The pasted / typed link (`value` when called from paste or the `?v=`
  // deep link, else the input's current text), or else the picked file.
  // Toasts and returns null when there's nothing usable.
  const resolveInput = (value?: string): SignupDialogInput | null => {
    const videoUrl = value ?? urlInputRef.current?.value;

    if (videoUrl) {
      const source = parseVideoSource(videoUrl);
      // Reject non-URLs and provider-host URLs that aren't single-video
      // links (Drive home/folders, Twitch channels, playlists, ...).
      if (!source || (source.provider !== "direct" && !source.id)) {
        posthog?.capture("Invalid video URL", { url: videoUrl });
        toast.error(m.invalidLink);
        return null;
      }

      // Store the processed URL in value state
      setValue(videoUrl);
      return { type: "url", value: videoUrl };
    }

    const videoFile = fileInputRef.current?.files?.[0];

    if (!videoFile) {
      toast.error(m.uploadFirst);
      return null;
    }
    // disable this for now as it makes the browser crash sometimes, to investigate later
    // try {
    //   if (
    //     profile &&
    //     !(await validateFileVideoDuration(
    //       videoFile,
    //       profile.uploadDurationAllowanceSeconds
    //     ))
    //   )
    //     return showVideoTooLongDialog();
    // } catch (error) {

    //   return toast.error("Error");
    // }
    return { type: "file", value: videoFile };
  };

  // The old signed-out flow, and the signup-modal-flow experiment's control
  // arm: /signup, carrying the link (and task) to /spaces, where it reopens
  // the submit form once signed in. A picked file can't make the trip.
  const redirectToSignup = (videoUrl?: string) => {
    toast(m.signIn);
    const target = new URL(
      buildSignupRedirectUrl(window.location.origin, {
        videoUrl,
        task: readTaskParam(),
      })
    );
    router.push(
      `/signup?redirect=${encodeURIComponent(target.pathname + target.search)}`
    );
  };

  // The visitor's signup-modal-flow arm. Reading the flag records the
  // exposure. Waits for flags on a first visit (a paste right after page
  // load), under the input's spinner; null if cancelled meanwhile.
  const getSignupFlowVariant = async (): Promise<SignupFlowVariant | null> => {
    // Local dev gets the dialog without a flag call (so no exposure), unless
    // overridden from the console:
    //   posthog.featureFlags.overrideFeatureFlags({ flags: { "signup-modal-flow": "control" } })
    if (process.env.NODE_ENV === "development") {
      const overrides = posthog?.get_property?.(
        "$override_feature_flags"
      ) as Record<string, string | boolean> | undefined;
      return signupFlowVariantFor(overrides?.[SIGNUP_FLOW_FLAG_KEY] ?? "test");
    }
    // Not part of the experiment: today's flow, no exposure.
    if (isReturningVisitor(posthog?.get_property?.("$user_state"))) {
      return "control";
    }
    const requestId = ++dialogLoadRequestRef.current;
    setIsLoadingDialog(true);
    await waitForFeatureFlags(posthog);
    if (dialogLoadRequestRef.current !== requestId) return null;
    setIsLoadingDialog(false);
    return signupFlowVariantFor(posthog?.getFeatureFlag(SIGNUP_FLOW_FLAG_KEY));
  };

  const handleSubmit = useCallback(
    async (value?: string) => {
      let activeSession = session;
      if (!activeSession) {
        // No session in context: signed out, or still being read from
        // storage right after page load. getSession() waits for the
        // Supabase client to initialize, so a signed-in visitor who pastes
        // straight away isn't shown the signup dialog.
        const requestId = ++dialogLoadRequestRef.current;
        setIsLoadingDialog(true);
        const { data } = await supabaseClient.auth.getSession();
        if (dialogLoadRequestRef.current !== requestId) return; // cancelled
        setIsLoadingDialog(false);
        activeSession = data.session;
      }

      const isEmpty =
        !(value ?? urlInputRef.current?.value) &&
        !fileInputRef.current?.files?.[0];
      // Signed out and nothing picked yet (the CTA used as a "get started"
      // button): no video to hold on to, so straight to /signup.
      if (isEmpty && !activeSession) return redirectToSignup();

      const input = resolveInput(value);
      if (!input) return;

      if (activeSession) return void openSubmitForm(input, activeSession);

      // "signup-modal-flow" experiment (helpers/signupFlowExperiment.ts),
      // decided here for every signed-out submit with a usable video, so
      // exposure means the same thing in both arms.
      const variant = await getSignupFlowVariant();
      if (!variant) return; // cancelled
      if (variant === "test") return void openSignupDialog(input);
      redirectToSignup(input.type === "url" ? input.value : undefined);
    },
    [openSignupDialog, openSubmitForm, posthog, router, session, supabaseClient]
  );

  // handle Submit on Enter
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const validateFileFormat = (file: File) => {
    const allowedFormats = ["video/mp4", "video/webm", "video/quicktime"];
    if (!allowedFormats.includes(file.type)) {
      posthog?.capture("Invalid file format", {
        file: file,
      });
      return false;
    }
    return true;
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const validFormat = validateFileFormat(event.target.files?.[0] as File);

    if (!validFormat) {
      clearInputs();
      return toast.error(m.fileTypes);
    }

    const file = event.target.files?.[0];
    if (!file) return;

    // If file > 10 gb
    if (file.size > 10 * 1024 * 1024 * 1024) {
      posthog?.capture("File too large", {
        size: file.size,
      });
      return toast.error(m.fileTooLarge);
    }

    setFile(file);
    handleSubmit();
  };

  const clearInputs = () => {
    // Cancel any in-flight dialog preload so the dialog never opens
    // after the user cleared the input.
    dialogLoadRequestRef.current++;
    setIsLoadingDialog(false);

    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    setValue(null);
    if (urlInputRef.current) {
      urlInputRef.current.value = "";
      urlInputRef.current.focus();
    }
  };

  // Listen for keydown event
  useEffect(() => {
    const currentRef = urlInputRef.current;
    currentRef?.addEventListener("keydown", handleKeyDown);
    return () => {
      currentRef?.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const processedRef = useRef(false); // Ref to track if the current query has been processed
  const pathname = usePathname();

  // We only need `?v=` once at mount to seed a deep-linked submit. Reading
  // `window.location.search` directly inside the effect is sufficient and
  // avoids `useSearchParams()`, which forces a CSR bailout that previously
  // surfaced as a `<Suspense>` skeleton flashing on the home page hero.
  useEffect(() => {
    if (!session) return;
    if (!pathname) return;
    if (typeof window === "undefined") return;
    if (processedRef.current) return;

    const qs = new URLSearchParams(window.location.search);
    const vParam = qs.get("v");
    if (!vParam) return;

    processedRef.current = true;
    setValue(vParam);
    handleSubmit(vParam);

    qs.delete("v");
    const newUrl = qs.toString() ? `${pathname}?${qs.toString()}` : pathname;
    router.replace(newUrl);
  }, [pathname, setValue, handleSubmit, session, router]);

  const FileItem = ({ file }: { file: File }) => {
    return (
      <div className="flex flex-1 items-center justify-start overflow-hidden">
        <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">
          {file.name}
        </span>
      </div>
    );
  };

  // Rolling provider name in the placeholder. Paused whenever the animated
  // overlay isn't visible (focus keeps it visible but frozen). Tool pages
  // that pass `placeholderText` keep their static prompt, and
  // prefers-reduced-motion swaps to a static generic sentence.
  const placeholderPaused =
    isFocused || !!value || !!file || showDropArea || !!placeholderText;
  const placeholderProviders = [...PLACEHOLDER_PROVIDERS, m.anyVideoUrl];
  const { index: providerIndex, reducedMotion } = useRollingIndex(
    placeholderProviders.length,
    2500,
    placeholderPaused
  );
  const showRollingPlaceholder =
    !placeholderText && !showDropArea && !reducedMotion && !value && !file;

  const URLInput = (
    <div className="relative flex-1 w-full flex items-center overflow-hidden">
      <input
        autoComplete="off"
        ref={urlInputRef}
        type="text"
        id="search-bar"
        value={value || ""}
        aria-label={m.placeholderStatic}
        placeholder={
          showDropArea
            ? m.drop
            : placeholderText ?? (reducedMotion ? m.placeholderStatic : "")
        }
        className={`pr-3 pl-0 py-2 w-full flex-1 outline-none bg-transparent ${showDropArea
          ? "placeholder:text-purple-500"
          : "placeholder:text-slate-400"
          }`}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(e) => {
          setValue(e.target.value);
        }}
      onPaste={(e) => {
        const pastedText = e.clipboardData.getData("text");
        const source = parseVideoSource(pastedText);

        if (source && (source.provider === "direct" || source.id)) {
          // We take over: handleSubmit sets the controlled value itself, so
          // letting the browser's default paste run too would insert the
          // URL a second time.
          e.preventDefault();
          setValue(pastedText);
          handleSubmit(pastedText);
        } else {
          // Let the default paste land in the input, but tell the user why
          // nothing auto-submitted.
          posthog?.capture("Invalid video URL", { url: pastedText });
          toast.error(m.invalidLink);
        }
      }}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      />
      {showRollingPlaceholder && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 flex items-center text-slate-400 overflow-hidden"
        >
          {/* Single inline-flow child so the space between the prefix and the
              token survives (flex item boundaries strip trailing spaces). */}
          <span className="whitespace-nowrap">
            {m.placeholderPrefix}
            <span
              key={providerIndex}
              className="inline-block animate-roll-in"
            >
              {placeholderProviders[providerIndex]}
            </span>
          </span>
        </span>
      )}
    </div>
  );

  const FileInput = (
    <input
      id="file-upload"
      className={`w-full h-full absolute inset-0 opacity-0 flex-1 text-start whitespace-nowrap text-ellipsis overflow-hidden ${showDropArea ? "text-violet-500" : ""
        } ${showDropArea ? "" : "hidden"}`}
      type="file"
      accept="video/mp4,video/quicktime,video/webm"
      onChange={handleFileChange}
      ref={fileInputRef}
      onDragLeave={() => {
        setShowDropArea(false);
      }}
    />
  );

  return (
    <label
      className={`${rest.className}  ${showDropArea
        ? "bg-violet-200 border-violet-600 text-violet-600"
        : "bg-white"
        } relative flex flex-col sm:flex-row items-center justify-center border p-1.5 rounded-2xl gap-2`}
      htmlFor="search-bar"
      onDragEnter={() => {
        setShowDropArea(true);
      }}
      onDrop={() => setShowDropArea(false)}
    >
      <div className="flex items-center flex-1 w-full gap-2 overflow-hidden">
        <Button
          variant="tertiary"
          className={`${showDropArea ? "pointer-events-none" : ""}`}
          onClick={() => {
            fileInputRef.current?.click();
          }}
          data-tooltip-content={m.uploadTooltip}
          data-tooltip-id="global"
          size="l"
        >
          <ArrowUpTrayIcon
            className={` ${showDropArea ? "text-violet-500" : "text-slate-500"
              }`}
          />
        </Button>

        {file ? <FileItem file={file} /> : URLInput}
        {FileInput}
        {(value || file || isLoadingDialog) && (
          <Button
            size="m"
            variant="tertiary"
            className="group/clear"
            onClick={clearInputs}
            data-tooltip-id={isLoadingDialog ? "global" : undefined}
            data-tooltip-content={isLoadingDialog ? m.cancel : undefined}
          >
            {isLoadingDialog ? (
              <Button.Content>
                <Spinner className="w-5 h-5 text-slate-500 group-hover/clear:hidden" />
                <XMarkIcon className="w-5 h-5 text-slate-500 hidden group-hover/clear:block" />
              </Button.Content>
            ) : (
              <XMarkIcon
                className={`w-5 h-5 ${showDropArea ? "text-violet-500" : "text-slate-500"
                  }`}
              />
            )}
          </Button>
        )}
      </div>
      <Button
        className={`w-full sm:w-auto ${showDropArea ? "pointer-events-none" : ""
          }`}
        disabled={disabled}
        onClick={() => handleSubmit()}
        size="l"
      >
        <SparklesIcon className="w-3 h-3" />
        {ctaLabel ?? m.generate}
      </Button>
    </label>
  );
};

export default VideoInput;
